import type {
  LoginRequest,
  LoginResponse,
  MeResponse,
  LogoutResponse,
  LogoutFromStoreResponse,
  UpdatePasswordRequest,
  UpdatePasswordResponse,
  AuthState,
} from "../types/auth";
import { formatApiError } from "./error-message";
import { ApiError } from "./client";

// Build an ApiError that carries the parsed body + status, so callers
// like LoginPage can inspect structured fields (e.g. 409 deviceId
// conflict) without having to re-parse err.message as JSON.
function buildAuthError(
  status: number,
  statusText: string,
  body: unknown,
): ApiError {
  const err = new ApiError(status, statusText, body);
  err.message = formatApiError(body, status, statusText);
  return err;
}

let baseUrl = "";
const API_PREFIX = "/api/v1";

export function setAuthBaseUrl(url: string): void {
  baseUrl = url.replace(/\/$/, "");
}

async function postJson<T>(
  endpoint: string,
  data: Record<string, unknown>,
  headers?: Record<string, string>,
): Promise<T> {
  const url = `${baseUrl}${API_PREFIX}${endpoint}`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    if (response.status === 401) {
      handleAuthError();
    }
    const errorBody = await response.json().catch(() => null);
    throw buildAuthError(response.status, response.statusText, errorBody);
  }

  return response.json() as Promise<T>;
}

async function fetchJson<T>(
  endpoint: string,
  headers?: Record<string, string>,
): Promise<T> {
  const url = `${baseUrl}${API_PREFIX}${endpoint}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
  });

  if (!response.ok) {
    if (response.status === 401) {
      handleAuthError();
    }
    const errorBody = await response.json().catch(() => null);
    throw buildAuthError(response.status, response.statusText, errorBody);
  }

  return response.json() as Promise<T>;
}

async function deleteJson<T>(
  endpoint: string,
  headers?: Record<string, string>,
): Promise<T> {
  const url = `${baseUrl}${API_PREFIX}${endpoint}`;

  const response = await fetch(url, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
  });

  if (!response.ok) {
    if (response.status === 401) {
      handleAuthError();
    }
    const errorBody = await response.json().catch(() => null);
    throw buildAuthError(response.status, response.statusText, errorBody);
  }

  return response.json() as Promise<T>;
}

export async function login(request: LoginRequest): Promise<LoginResponse> {
  return postJson<LoginResponse>("/auth/login", {
    userName: request.userName,
    password: request.password,
    deviceId: request.deviceId,
    shopId: request.shopId,
    version: request.version,
    skipStoreSelection: request.skipStoreSelection,
    sessionTimeout: request.sessionTimeout,
  });
}

export async function getMe(): Promise<MeResponse> {
  return fetchJson<MeResponse>("/auth/me", getAuthHeaders());
}

export async function logout(): Promise<LogoutResponse> {
  return deleteJson<LogoutResponse>("/auth/logout", getAuthHeaders());
}

export async function logoutFromStore(
  shopId: string,
  userName: string,
): Promise<LogoutFromStoreResponse> {
  return deleteJson<LogoutFromStoreResponse>(
    `/auth/logout/${encodeURIComponent(shopId)}/${encodeURIComponent(userName)}`,
  );
}

export async function updatePassword(
  request: UpdatePasswordRequest,
): Promise<UpdatePasswordResponse> {
  return postJson<UpdatePasswordResponse>(
    "/auth/update-password",
    {
      userId: request.userId,
      newpass: request.newpass,
      shopId: request.shopId,
    },
    getAuthHeaders(),
  );
}

// --- Auth header injection & error handling ---

let authStoreRef: {
  getState: () => Pick<
    AuthState,
    | "token"
    | "tokenExpiry"
    | "deviceId"
    | "user"
    | "store"
    | "clearSession"
    | "isTokenExpired"
  >;
} | null = null;

export function registerAuthStore(store: typeof authStoreRef): void {
  authStoreRef = store;
}

export function getAuthHeaders(): Record<string, string> {
  if (!authStoreRef) return {};
  const state = authStoreRef.getState();
  const { token, deviceId, user } = state;
  if (!token || !user) return {};
  if (state.isTokenExpired()) {
    state.clearSession();
    return {};
  }
  return {
    "x-access-token": token,
    "x-auth-device": deviceId,
    "x-auth-user": user.userId,
  };
}

/** @deprecated Used by unmigrated old-API endpoints (billing). Remove once billing migrates. */
export function getAuthFormData(): URLSearchParams | null {
  if (!authStoreRef) return null;
  const state = authStoreRef.getState();
  const { token, deviceId, user, store } = state;
  if (!token || !user || !store) return null;
  if (state.isTokenExpired()) {
    state.clearSession();
    return null;
  }
  const params = new URLSearchParams();
  params.append("shop", store.shopId);
  params.append("userId", user.userId);
  params.append("userName", user.userName);
  params.append("token", token);
  params.append("deviceId", deviceId);
  return params;
}

export function handleAuthError(): boolean {
  if (!authStoreRef) return false;
  const state = authStoreRef.getState();
  if (state.token) {
    state.clearSession();
    return true;
  }
  return false;
}
